import { buildApiUrl } from "../utils/apiConfig";

export type BulkProvisionFieldError = {
    row: number;
    field: string;
    message: string;
};

/** Backend field headers — keep in sync with BulkAthleteProvisionService (exact, including the "(required)" suffix). */
export const BULK_TEMPLATE_HEADERS = [
    "Student / Athlete Email Address (required)",
    "First name (required)",
    "Last name (required)",
    "Parent/Guardian Email Address (required)",
    "Admin/Staff/Coach Email Address (required)",
] as const;

const FIELD_ORDER = BULK_TEMPLATE_HEADERS;

const BULK_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeHeaderCell(value: string): string {
    return String(value ?? "").replace(/\ufeff/g, "").trim();
}

function blankFieldError(row: number, columnIndex: number): BulkProvisionFieldError {
    return { row, field: BULK_TEMPLATE_HEADERS[columnIndex], message: "Cannot be blank." };
}

function invalidEmailError(row: number, columnIndex: number): BulkProvisionFieldError {
    return { row, field: BULK_TEMPLATE_HEADERS[columnIndex], message: "Must be a valid email address." };
}

/**
 * Client-side pre-flight validation that mirrors the server's structural rules so users get instant,
 * friendly feedback and we avoid a round-trip on obviously-bad files. The server remains the source of
 * truth for existence, role, company, and guardian-account checks.
 */
export function validateBulkMatrix(matrix: string[][]): BulkProvisionFieldError[] {
    const header = matrix[0] ?? [];
    const headerErrors: BulkProvisionFieldError[] = [];
    BULK_TEMPLATE_HEADERS.forEach((expected, i) => {
        const actual = normalizeHeaderCell(header[i] ?? "");
        if (actual.toLowerCase() !== expected.toLowerCase()) {
            headerErrors.push({ row: 1, field: expected, message: `Expected header "${expected}".` });
        }
    });
    if (headerErrors.length > 0) {
        return headerErrors;
    }

    const errors: BulkProvisionFieldError[] = [];
    let sawDataRow = false;
    for (let r = 1; r < matrix.length; r++) {
        const cells = BULK_TEMPLATE_HEADERS.map((_, i) => String(matrix[r]?.[i] ?? "").trim());
        if (cells.every((c) => c === "")) {
            continue; // Fully blank row — the server skips these.
        }
        sawDataRow = true;
        const spreadsheetRow = r + 1;
        const [student, first, last, guardian, assignee] = cells;

        if (!student) {
            errors.push(blankFieldError(spreadsheetRow, 0));
        } else if (!BULK_EMAIL_RE.test(student)) {
            errors.push(invalidEmailError(spreadsheetRow, 0));
        }
        if (!first) {
            errors.push(blankFieldError(spreadsheetRow, 1));
        }
        if (!last) {
            errors.push(blankFieldError(spreadsheetRow, 2));
        }
        if (!guardian) {
            errors.push(blankFieldError(spreadsheetRow, 3));
        } else if (!BULK_EMAIL_RE.test(guardian)) {
            errors.push(invalidEmailError(spreadsheetRow, 3));
        }
        if (!assignee) {
            errors.push(blankFieldError(spreadsheetRow, 4));
        } else if (!BULK_EMAIL_RE.test(assignee)) {
            errors.push(invalidEmailError(spreadsheetRow, 4));
        }
    }

    if (!sawDataRow) {
        return [
            {
                row: 1,
                field: BULK_TEMPLATE_HEADERS[0],
                message: "No data rows found. Add at least one student row.",
            },
        ];
    }
    return errors;
}

/**
 * Groups row-level API errors into one human-readable line per field/column
 * so users see e.g. "12 rows — Student / Athlete Email Address: …" instead of dozens of duplicates.
 */
export function summarizeBulkValidationErrors(errors: BulkProvisionFieldError[]): string[] {
    if (!errors.length) {
        return [
            "We could not validate the file. Check that it is a .csv or .xlsx matching the template, then try again.",
        ];
    }

    const byField = new Map<string, BulkProvisionFieldError[]>();
    for (const e of errors) {
        const key = (e.field && e.field.trim()) || "File";
        if (!byField.has(key)) {
            byField.set(key, []);
        }
        byField.get(key)!.push(e);
    }

    const lines: string[] = [];
    const used = new Set<string>();

    for (const field of FIELD_ORDER) {
        const list = byField.get(field);
        if (!list?.length) {
            continue;
        }
        used.add(field);
        lines.push(summarizeGroup(field, list));
    }

    for (const [field, list] of byField) {
        if (used.has(field)) {
            continue;
        }
        lines.push(summarizeGroup(field, list));
    }

    return lines;
}

function summarizeGroup(field: string, list: BulkProvisionFieldError[]): string {
    const n = list.length;
    const rowWord = n === 1 ? "row" : "rows";
    const msgs = [...new Set(list.map((e) => e.message.trim()))];

    if (msgs.length === 1) {
        return `${n} ${rowWord} — ${field}: ${friendlySingleCause(msgs[0])}`;
    }

    const hints = msgs.map((m) => friendlySingleCause(m)).filter(Boolean);
    const uniqueHints = [...new Set(hints)];
    if (uniqueHints.length <= 3) {
        return `${n} ${rowWord} — ${field}: ${uniqueHints.join("; ")}`;
    }
    return `${n} ${rowWord} — ${field}: several different issues; check each value against the rules above.`;
}

function friendlySingleCause(backendMessage: string): string {
    const trimmed = backendMessage.trim();
    const m = trimmed.toLowerCase();
    if (
        trimmed.includes("@") &&
        (m.includes("already in use") ||
            m.includes("already exists") ||
            m.includes("already registered") ||
            m.includes("duplicate email"))
    ) {
        return trimmed;
    }
    if (m.includes("cannot be blank")) {
        return "value is missing or empty";
    }
    if (m.includes("must be a valid email")) {
        return "email format is invalid";
    }
    if (m.includes("already exists")) {
        return "this email is already registered";
    }
    if (m.includes("duplicate email")) {
        return "this email appears more than once in the file";
    }
    if (m.includes("letters only") || m.includes("2 and 30")) {
        return "use letters only, 2–30 characters";
    }
    if (m.includes("must be different from the athlete email")) {
        return "parent/guardian email must differ from the athlete email";
    }
    if (m.includes("matches a student email")) {
        return "parent/guardian email matches a student email in your file";
    }
    if (m.includes("not a parent/guardian")) {
        return "this email already belongs to an account that is not a parent/guardian";
    }
    if (m.includes("no user found")) {
        return "no account matches this email";
    }
    if (m.includes("admin, staff, or coach") || m.includes("staff or administrator") || m.includes("administrator role")) {
        return "this user must be an Admin, Staff, or Coach";
    }
    if (m.includes("same company") || m.includes("same company or school")) {
        return "the team member must belong to your company or school";
    }
    if (m.includes("header") || m.includes("five columns") || m.includes("four columns") || m.includes("expected header")) {
        return backendMessage;
    }
    if (m.includes("no data rows")) {
        return "add at least one data row below the header";
    }
    if (m.includes("missing header")) {
        return "the first row must be the column headers";
    }
    if (m.includes("does not have 5 columns") || m.includes("does not have 4 columns")) {
        return "each row needs five columns";
    }
    return backendMessage;
}

/**
 * @param staffEmail When set (e.g. team view), server adds a sample row with this Admin/Staff/Coach Email Address
 */
export async function downloadBulkProvisionTemplate(staffEmail?: string | null): Promise<void> {
    const base = buildApiUrl("api/athletes/bulk-provision/template");
    const trimmed = staffEmail?.trim();
    const fetchUrl =
        trimmed && trimmed.length > 0
            ? `${base}?staffEmail=${encodeURIComponent(trimmed)}`
            : base;
    const res = await fetch(fetchUrl, {
        credentials: "include",
    });
    if (res.status === 401) {
        window.location.href = "/login";
        return;
    }
    if (!res.ok) {
        throw new Error(`Failed to download template (HTTP ${res.status})`);
    }
    triggerBlobDownload(await res.blob(), "athlete-bulk-template.xlsx");
}

export type BulkProvisionFailureReasonApi = "VALIDATION" | "DUPLICATE" | "OTHER";

export type BulkProvisionIssue = {
    row: number;
    field: string;
    message: string;
    reason: BulkProvisionFailureReasonApi;
};

/** Successful HTTP 200 response from bulk-provision (may include partial failures). */
export type BulkProvisionUploadResult = {
    createdCount: number;
    failedRowCount: number;
    failures: BulkProvisionIssue[];
};

export type BulkProvisionUploadFailure = {
    status: number;
    validationErrors?: BulkProvisionFieldError[];
    limitMessage?: string;
    detail?: string;
    /** API title (e.g. generic); prefer validationErrors + summarize for user-facing copy */
    title?: string;
    /** When a multi-part upload loses the connection after some parts succeeded */
    approximateCreated?: number;
};

/** Hard cap on students (data rows) per bulk upload — guards performance and request timeouts. */
export const BULK_MAX_STUDENTS = 100;
export const BULK_MAX_STUDENTS_MESSAGE = `Bulk upload is limited to ${BULK_MAX_STUDENTS} students at a time.`;

export const BULK_UPLOAD_GATEWAY_TIMEOUT_MESSAGE =
    "Your network or browser stopped waiting before the server finished, but some athletes may still have been saved. " +
    "Open the athlete list again to verify, then upload any that are missing.";

export const BULK_UPLOAD_NETWORK_ERROR_MESSAGE =
    "We could not reach the server to finish the upload. Check your connection, refresh the athlete list to see if any rows were saved, then try again.";

const BULK_UPLOAD_SERVER_ERROR_MESSAGE =
    "We could not complete the upload. Please try again, or contact support if the problem continues.";

function userFacingServerDetail(detail: string | undefined): string {
    if (!detail?.trim()) {
        return BULK_UPLOAD_SERVER_ERROR_MESSAGE;
    }
    const lower = detail.trim().toLowerCase();
    if (
        lower.includes("could not initialize proxy") ||
        lower.includes("lazyinitialization") ||
        lower.includes("no session") ||
        lower.includes("hibernate") ||
        lower.includes("sql") ||
        lower.includes("exception") ||
        lower.includes("internal server error")
    ) {
        return BULK_UPLOAD_SERVER_ERROR_MESSAGE;
    }
    return detail.trim();
}

export function reasonLabelForBulkIssue(reason: BulkProvisionFailureReasonApi): string {
    switch (reason) {
        case "DUPLICATE":
            return "Duplicate";
        case "VALIDATION":
            return "Validation";
        case "OTHER":
            return "Error";
        default:
            return reason;
    }
}

/** How many skipped-row lines to show before “Show all”. */
export const BULK_SKIPPED_PREVIEW_LIMIT = 4;

export const BULK_SKIPPED_DOWNLOAD_FILENAME = "skipped-athletes.xlsx";

export const BULK_FILE_TYPE_MESSAGE = "Use a .csv or .xlsx file.";

export const BULK_ONE_FILE_MESSAGE = "Upload one file at a time.";

export const BULK_SKIPPED_DOWNLOAD_EMPTY_MESSAGE = "There are no skipped rows to download.";

export const BULK_SKIPPED_DOWNLOAD_FAILED_MESSAGE =
    "We could not rebuild the skipped rows from your file. Copy the errors above and fix your original spreadsheet.";

/** True for the spreadsheet types the bulk-upload dialog accepts. */
export function isAcceptedBulkFileName(name: string): boolean {
    const lower = name.toLowerCase();
    return lower.endsWith(".csv") || lower.endsWith(".xlsx");
}

/** Counts non-blank data rows (ignores the header row and fully-empty rows). */
export function countBulkDataRows(matrix: string[][]): number {
    let count = 0;
    for (let r = 1; r < matrix.length; r++) {
        const cells = matrix[r] ?? [];
        if (cells.some((c) => String(c ?? "").trim() !== "")) {
            count++;
        }
    }
    return count;
}

/** Parses a spreadsheet to a cell matrix, or null if it cannot be read. */
export async function readBulkFileMatrix(file: File): Promise<string[][] | null> {
    try {
        return trimTrailingBlankRows(await parseBulkFileToMatrix(file));
    } catch {
        return null;
    }
}

/** Parses a spreadsheet and returns how many student rows it has, or null if it cannot be read. */
export async function countBulkFileDataRows(file: File): Promise<number | null> {
    const matrix = await readBulkFileMatrix(file);
    return matrix ? countBulkDataRows(matrix) : null;
}

/** Short result line after a successful create (matches the results dialog). */
export function bulkCreatedHeadline(createdCount: number): string | null {
    if (createdCount <= 0) {
        return null;
    }
    if (createdCount === 1) {
        return "1 athlete added. Welcome email sent.";
    }
    return `${createdCount} athletes added. Welcome emails sent.`;
}

/** Short result line for skipped rows (matches the results dialog). */
export function bulkSkippedHeadline(failedRowCount: number): string | null {
    if (failedRowCount <= 0) {
        return null;
    }
    if (failedRowCount === 1) {
        return "1 row skipped. Fix it and upload again.";
    }
    return `${failedRowCount} rows skipped. Fix them and upload again.`;
}

/** One issue per spreadsheet row, first failure wins, sorted by row number. */
export function uniqueFailuresByRow(failures: BulkProvisionIssue[]): BulkProvisionIssue[] {
    const seen = new Set<number>();
    const unique: BulkProvisionIssue[] = [];
    for (const failure of failures) {
        if (seen.has(failure.row)) {
            continue;
        }
        seen.add(failure.row);
        unique.push(failure);
    }
    return unique.sort((a, b) => a.row - b.row);
}

function emailFromBulkText(text: string): string {
    const match = String(text || "").match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    return match?.[0] ?? "";
}

function emailColumnLabel(field: string): string {
    const f = (field || "").toLowerCase();
    if (f.includes("parent") || f.includes("guardian")) {
        return "parent email";
    }
    if (f.includes("student") || f.includes("athlete")) {
        return "athlete email";
    }
    return "Admin/Staff/Coach email";
}

/** Spreadsheet column for a bulk-upload field header (0-based). */
export function columnIndexForBulkField(field: string): number {
    const f = (field || "").toLowerCase();
    if (f.includes("parent") || f.includes("guardian")) {
        return 3;
    }
    if (f.includes("admin") || f.includes("staff") || f.includes("coach")) {
        return 4;
    }
    if (f.includes("student") || f.includes("athlete")) {
        return 0;
    }
    if (f.includes("first")) {
        return 1;
    }
    if (f.includes("last")) {
        return 2;
    }
    return -1;
}

/** Cell value from the original file for a skipped-row issue, if the matrix is available. */
export function cellValueForBulkIssue(
    matrix: string[][] | null | undefined,
    issue: BulkProvisionIssue
): string {
    if (!matrix || issue.row < 2) {
        return "";
    }
    const col = columnIndexForBulkField(issue.field);
    if (col < 0) {
        return "";
    }
    return String(matrix[issue.row - 1]?.[col] ?? "").trim();
}

/** User-facing reason for a skipped row — short, no field/API jargon. */
export function friendlySkippedRowMessage(issue: BulkProvisionIssue, cellValue?: string): string {
    const field = (issue.field || "").toLowerCase();
    const message = (issue.message || "").toLowerCase();
    const quoted = (cellValue || emailFromBulkText(issue.message) || "").trim();

    if (
        message.includes("already in use") ||
        message.includes("already exists") ||
        message.includes("already registered") ||
        message.includes("duplicate email") ||
        issue.reason === "DUPLICATE"
    ) {
        return quoted ? `${quoted} is already registered` : "Email is already registered";
    }
    if (
        message.includes("must be different from the athlete") ||
        message.includes("matches a student email")
    ) {
        return quoted
            ? `Parent email ${quoted} matches the athlete email`
            : "Parent email matches athlete email";
    }
    if (
        message.includes("admin, staff, or coach") ||
        message.includes("staff or administrator") ||
        message.includes("administrator role")
    ) {
        return quoted
            ? `${quoted} isn't an Admin, Staff or Coach`
            : "Coach email isn't an Admin, Staff or Coach";
    }
    if (message.includes("no user found") || message.includes("no account")) {
        return quoted
            ? `No account matches ${emailColumnLabel(issue.field)} ${quoted}`
            : "No account matches this email";
    }
    if (message.includes("same company") || message.includes("same company or school")) {
        return quoted
            ? `${quoted} is not in your company or school`
            : "Team member must belong to your company or school";
    }
    if (message.includes("not a parent/guardian")) {
        return quoted
            ? `${quoted} already belongs to an account that is not a parent/guardian`
            : "This email already belongs to an account that is not a parent/guardian";
    }
    if (message.includes("cannot be blank") || message.includes("is missing")) {
        if (field.includes("student") || field.includes("athlete email")) {
            return "Athlete email is missing";
        }
        if (field.includes("first")) {
            return "First name is missing";
        }
        if (field.includes("last")) {
            return "Last name is missing";
        }
        if (field.includes("parent") || field.includes("guardian")) {
            return "Parent email is missing";
        }
        if (field.includes("admin") || field.includes("staff") || field.includes("coach")) {
            return "Coach email is missing";
        }
        return "A required value is missing";
    }
    if (message.includes("must be a valid email") || message.includes("valid email")) {
        if (field.includes("student") || field.includes("athlete")) {
            return quoted ? `Athlete email ${quoted} is invalid` : "Athlete email is invalid";
        }
        if (field.includes("parent") || field.includes("guardian")) {
            return quoted ? `Parent email ${quoted} is invalid` : "Parent email is invalid";
        }
        if (field.includes("admin") || field.includes("staff") || field.includes("coach")) {
            return quoted ? `Coach email ${quoted} is invalid` : "Coach email is invalid";
        }
        return quoted ? `${quoted} is not a valid email` : "Email format is invalid";
    }
    if (message.includes("letters only") || message.includes("2 and 30")) {
        if (field.includes("first")) {
            return "First name must be letters only, 2–30 characters";
        }
        if (field.includes("last")) {
            return "Last name must be letters only, 2–30 characters";
        }
        return "Use letters only, 2–30 characters";
    }
    return friendlySingleCause(issue.message);
}

export function formatSkippedRowLine(issue: BulkProvisionIssue, cellValue?: string): string {
    return `Row ${issue.row} · ${friendlySkippedRowMessage(issue, cellValue)}`;
}

/**
 * Header plus the original spreadsheet rows that failed (1-based row numbers).
 * Extra columns are dropped so the file can be fixed and uploaded again.
 */
export function extractSkippedRows(matrix: string[][], failedRows: number[]): string[][] {
    const headerSource = matrix[0] ?? [];
    const header = BULK_TEMPLATE_HEADERS.map((_, i) => {
        const cell = normalizeHeaderCell(String(headerSource[i] ?? ""));
        return cell || BULK_TEMPLATE_HEADERS[i];
    });
    const wanted = [...new Set(failedRows.filter((row) => Number.isFinite(row) && row >= 2))].sort(
        (a, b) => a - b
    );
    const body = wanted
        .filter((row) => row <= matrix.length)
        .map((row) => {
            const cells = matrix[row - 1] ?? [];
            return BULK_TEMPLATE_HEADERS.map((_, i) => String(cells[i] ?? "").trim());
        });
    return [header, ...body];
}

function triggerBlobDownload(blob: Blob, filename: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
}

/** Builds a re-uploadable .xlsx of only the skipped student rows. */
export async function buildSkippedRowsBlob(file: File, failedRows: number[]): Promise<Blob> {
    if (failedRows.length === 0) {
        throw new Error(BULK_SKIPPED_DOWNLOAD_EMPTY_MESSAGE);
    }
    let matrix: string[][];
    try {
        matrix = trimTrailingBlankRows(await parseBulkFileToMatrix(file));
    } catch {
        throw new Error(BULK_SKIPPED_DOWNLOAD_FAILED_MESSAGE);
    }
    const skipped = extractSkippedRows(matrix, failedRows);
    if (skipped.length < 2) {
        throw new Error(BULK_SKIPPED_DOWNLOAD_FAILED_MESSAGE);
    }
    const XLSX: typeof import("xlsx") = await import("xlsx");
    const sheet = XLSX.utils.aoa_to_sheet(skipped);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Skipped");
    const output = XLSX.write(book, { type: "array", bookType: "xlsx" }) as ArrayBuffer;
    return new Blob([output], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
}

export async function downloadSkippedRowsFile(
    file: File,
    failures: BulkProvisionIssue[]
): Promise<void> {
    const rows = uniqueFailuresByRow(failures).map((issue) => issue.row);
    const blob = await buildSkippedRowsBlob(file, rows);
    triggerBlobDownload(blob, BULK_SKIPPED_DOWNLOAD_FILENAME);
}

async function parseBulkFileToMatrix(file: File): Promise<string[][]> {
    const XLSX: typeof import("xlsx") = await import("xlsx");
    const lower = file.name.toLowerCase();
    const workbook =
        lower.endsWith(".csv") || lower.endsWith(".txt")
            ? XLSX.read(await file.text(), { type: "string" })
            : XLSX.read(await file.arrayBuffer(), { type: "array" });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        throw new Error("This file has no worksheet or data.");
    }
    const sheet = workbook.Sheets[sheetName];
    const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" }) as unknown[][];
    return matrix.map((row) => row.map((cell) => (cell === null || cell === undefined ? "" : String(cell))));
}

function trimTrailingBlankRows(matrix: string[][]): string[][] {
    let end = matrix.length;
    while (
        end > 0 &&
        matrix[end - 1].every((c) => !String(c ?? "").replace(/\u00a0/g, " ").trim())
    ) {
        end--;
    }
    return matrix.slice(0, end);
}

/** Friendly copy when the bounded server-side worker pool is full (HTTP 429). */
export const BULK_UPLOAD_AT_CAPACITY_MESSAGE =
    "Too many bulk uploads are in progress right now. Please wait a moment and try again.";

/** How often to poll the job status endpoint, and the safety ceiling before we stop waiting. */
export const BULK_JOB_POLL_INTERVAL_MS = 1000;
export const BULK_JOB_POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

type BulkProvisionJobStatusApi = {
    jobId: string;
    status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED";
    result?: { createdCount?: number; failedRowCount?: number; failures?: BulkProvisionIssue[] } | null;
    error?: string | null;
};

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Submits the upload (202 + jobId) and polls the status endpoint until the background job finishes.
 * Keeps the request thread free on the server while the UI shows progress. Returns the same union the
 * synchronous flow used, so the modal is unchanged: a {@link BulkProvisionUploadResult} on completion,
 * or a {@link BulkProvisionUploadFailure} for a rejected submission or a failed job.
 */
async function postBulkProvisionMultipart(file: File): Promise<BulkProvisionUploadResult | BulkProvisionUploadFailure> {
    const submitted = await submitBulkProvisionJob(file);
    if ("status" in submitted) {
        return submitted;
    }
    return pollBulkProvisionJob(submitted.jobId);
}

async function submitBulkProvisionJob(
    file: File
): Promise<{ jobId: string } | BulkProvisionUploadFailure> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(buildApiUrl("api/athletes/bulk-provision"), {
        method: "POST",
        body: fd,
        credentials: "include",
    });

    if (res.status === 401) {
        window.location.href = "/login";
        throw new Error("Unauthorized");
    }

    if (res.status === 202) {
        const data = (await res.json().catch(() => ({}))) as { jobId?: string };
        if (typeof data.jobId === "string" && data.jobId.length > 0) {
            return { jobId: data.jobId };
        }
        return { status: 202, detail: BULK_UPLOAD_SERVER_ERROR_MESSAGE };
    }

    return parseBulkFailureResponse(res);
}

async function pollBulkProvisionJob(
    jobId: string
): Promise<BulkProvisionUploadResult | BulkProvisionUploadFailure> {
    const deadline = Date.now() + BULK_JOB_POLL_TIMEOUT_MS;
    const url = buildApiUrl(`api/athletes/bulk-provision/${encodeURIComponent(jobId)}`);
    while (Date.now() < deadline) {
        const res = await fetch(url, { credentials: "include" });

        if (res.status === 401) {
            window.location.href = "/login";
            throw new Error("Unauthorized");
        }

        if (res.ok) {
            const data = (await res.json().catch(() => ({}))) as BulkProvisionJobStatusApi;
            if (data.status === "COMPLETED") {
                const result = data.result ?? {};
                return {
                    createdCount: result.createdCount ?? 0,
                    failedRowCount: result.failedRowCount ?? 0,
                    failures: Array.isArray(result.failures) ? result.failures : [],
                };
            }
            if (data.status === "FAILED") {
                const message =
                    data.error && data.error.trim().length > 0
                        ? data.error.trim()
                        : BULK_UPLOAD_SERVER_ERROR_MESSAGE;
                // Surface as a processed failure the modal shows as a friendly error.
                return { status: 422, limitMessage: message, detail: message };
            }
            // PENDING / RUNNING — keep waiting.
        } else if (res.status === 404) {
            return { status: 404, detail: BULK_UPLOAD_SERVER_ERROR_MESSAGE };
        } else if (res.status < 500) {
            return { status: res.status, detail: BULK_UPLOAD_SERVER_ERROR_MESSAGE };
        }
        // 5xx while polling is treated as transient — retry until the deadline.

        await sleep(BULK_JOB_POLL_INTERVAL_MS);
    }
    return { status: 0, detail: BULK_UPLOAD_GATEWAY_TIMEOUT_MESSAGE };
}

async function parseBulkFailureResponse(res: Response): Promise<BulkProvisionUploadFailure> {
    const data = (await res.json().catch(() => ({}))) as {
        errors?: BulkProvisionFieldError[];
        detail?: string;
        title?: string;
        message?: string;
        error?: string;
    };

    if (res.status === 400) {
        const rawErrors = Array.isArray(data.errors) ? data.errors : undefined;
        return {
            status: 400,
            validationErrors: rawErrors,
            detail: typeof data.detail === "string" ? data.detail : undefined,
            title: typeof data.title === "string" ? data.title : undefined,
        };
    }

    if (res.status === 422) {
        return {
            status: 422,
            limitMessage: typeof data.detail === "string" ? data.detail : undefined,
        };
    }

    if (res.status === 429) {
        return {
            status: 429,
            detail: typeof data.detail === "string" && data.detail.trim().length > 0
                ? data.detail
                : BULK_UPLOAD_AT_CAPACITY_MESSAGE,
        };
    }

    if (res.status === 403) {
        return {
            status: 403,
            detail: data.error === "File failed security scan" ? data.error : data.detail ?? "Request not allowed.",
        };
    }

    if (res.status === 502 || res.status === 503 || res.status === 504) {
        return {
            status: res.status,
            detail: BULK_UPLOAD_GATEWAY_TIMEOUT_MESSAGE,
        };
    }

    return {
        status: res.status,
        detail: userFacingServerDetail(data.detail ?? data.message),
    };
}

/**
 * Uploads a bulk file: submits the job (202 + jobId) then polls for completion. Files over
 * {@link BULK_MAX_STUDENTS} data rows are rejected client-side (the server enforces the same cap) to
 * keep each job well bounded. A full server-side worker pool surfaces as a friendly 429.
 */
export async function uploadBulkProvisionFile(
    file: File
): Promise<BulkProvisionUploadResult | BulkProvisionUploadFailure> {
    try {
        return await uploadBulkProvisionFileInternal(file);
    } catch (e: unknown) {
        if (e instanceof Error && e.message === "Unauthorized") {
            throw e;
        }
        const msg = e instanceof Error ? e.message : String(e);
        if (msg === "Failed to fetch" || msg.includes("NetworkError") || msg.includes("Load failed")) {
            return { status: 0, detail: BULK_UPLOAD_NETWORK_ERROR_MESSAGE };
        }
        return { status: 0, detail: BULK_UPLOAD_NETWORK_ERROR_MESSAGE };
    }
}

async function uploadBulkProvisionFileInternal(
    file: File
): Promise<BulkProvisionUploadResult | BulkProvisionUploadFailure> {
    const lower = file.name.toLowerCase();
    const isSpreadsheet = lower.endsWith(".csv") || lower.endsWith(".txt") || lower.endsWith(".xlsx");

    if (!isSpreadsheet) {
        return postBulkProvisionMultipart(file);
    }

    let matrix: string[][];
    try {
        matrix = trimTrailingBlankRows(await parseBulkFileToMatrix(file));
    } catch {
        return postBulkProvisionMultipart(file);
    }

    // Pre-flight structural validation (headers, required cells, email formats) before any upload.
    const clientErrors = validateBulkMatrix(matrix);
    if (clientErrors.length > 0) {
        return { status: 400, validationErrors: clientErrors };
    }

    // Enforce the per-upload student cap (the server enforces the same limit as the source of truth).
    if (countBulkDataRows(matrix) > BULK_MAX_STUDENTS) {
        return { status: 400, detail: BULK_MAX_STUDENTS_MESSAGE };
    }

    return postBulkProvisionMultipart(file);
}

